import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import { GetBuyItAgainProductsDocument } from 'src/gql/graphql';
import { parseProduct, Product } from 'src/utils/helpers/product';
import useCart from 'src/hooks/use-cart';
import useUser from 'src/hooks/use-user';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { tracker } from 'src/utils/analytics';
import useViewportVisibility, { ViewportVisibility } from 'src/hooks/use-viewport-visibility';
import { getItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/helpers/item-list-name';
import { ProductRecommendationSource } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

export type UseBuyItAgainCarouselReturn = {
  products: Product[];
  loading: boolean;
  handleViewAllClick: (destinationUrl: string) => void;
  viewportVisibility: ViewportVisibility;
};

export function useBuyItAgainCarousel(): UseBuyItAgainCarouselReturn {
  const router = useRouter();
  const cart = useCart();
  const user = useUser();
  const { User } = useStores();
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id;
  const menuType = useObserver(() => cart.menuType);
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const isPublicSessionPhoneLogin = useObserver(() => User?.isPublicSessionPhoneNumberLogin ?? false);
  const impressionTriggered = useRef(false);
  const viewportVisibility = useViewportVisibility();
  const { hasBeenVisible } = viewportVisibility;

  const { data, loading } = useQuery(GetBuyItAgainProductsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dispensaryId,
      menuType,
    },
    skip: !dispensaryId || !isLoggedIn || isPublicSessionPhoneLogin,
  });

  const trackerSource = router
    ? getItemListName({
        router,
        source: ProductRecommendationSource.native,
        section: 'Buy it again',
      })
    : null;

  const products = useMemo(
    () =>
      (data?.getBuyItAgainProducts.items ?? [])
        .filter((item) => !item.substitute)
        .map((item) => parseProduct(item.product)),
    [data?.getBuyItAgainProducts.items]
  );

  const shouldTriggerImpression = products.length > 0 && hasBeenVisible && !impressionTriggered.current;

  const handleImpression = useCallback(() => {
    if (!trackerSource) {
      return;
    }
    tracker.buyItAgainImpression({ products, trackerSource });
  }, [products, trackerSource]);

  useEffect(() => {
    if (shouldTriggerImpression) {
      handleImpression();
      impressionTriggered.current = true;
    }
  }, [handleImpression, shouldTriggerImpression]);

  const handleViewAllClick = useCallback(
    (destinationUrl: string): void => {
      if (!trackerSource) {
        return;
      }
      tracker.buyItAgainViewAllClicked({ destinationUrl, trackerSource });
    },
    [trackerSource]
  );

  return {
    products,
    loading,
    handleViewAllClick,
    viewportVisibility,
  };
}
